import { CodeXml, Type } from "lucide-react";
import React, { useState } from "react";

import { IgAlert } from "@intelligems/ig-theme/IgAlert";
import { IgCodeEditor } from "@intelligems/ig-theme/IgCodeEditor";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgToggleButtonGroup } from "@intelligems/ig-theme/IgToggleButtonGroup";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import {
  ModInsertType,
  TextEditMode,
} from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import { TextMod } from "@src/store/slices/experienceBuilder/types";
import { MenuItem, ToggleButton, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { ClampedText } from "@src/features/experienceBuilder/common/ClampedText";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDispatch, useSelector, useSelectorFactory } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { assert } from "@src/utils/assert";
import { ElementSelector } from "@src/features/experienceBuilder/modifications/basicMods/ElementSelector";
import { ElementMeta } from "@src/features/experienceBuilder/modifications/common/panel/ElementMeta";

const editModeButtons = [
  <ToggleButton
    value="text"
    key="text"
    size="small"
    sx={{
      flex: 1,
      gap: 1,
      fontWeight: 400,
      "&.Mui-selected": { fontWeight: 600 },
    }}
  >
    <Type size={16} />
    Edit text
  </ToggleButton>,
  <ToggleButton
    value="html"
    key="html"
    size="small"
    sx={{
      flex: 1,
      gap: 1,
      fontWeight: 400,
      "&.Mui-selected": { fontWeight: 600 },
    }}
  >
    <CodeXml size={16} />
    Edit HTML
  </ToggleButton>,
];

const htmlActionOptions: {
  value: ModInsertType;
  label: string;
  description: string;
}[] = [
  {
    value: "replace",
    label: "Replace",
    description: "Replace this element's full HTML",
  },
  {
    value: "beforebegin",
    label: "Prepend outside",
    description: "Add new HTML outside of this element, just before it.",
  },
  {
    value: "afterbegin",
    label: "Prepend inside",
    description: "Add new HTML inside of this element, at the top.",
  },
  {
    value: "beforeend",
    label: "Append inside",
    description: "Add new HTML inside of this element, at the bottom.",
  },
  {
    value: "afterend",
    label: "Append outside",
    description: "Add new HTML outside of this element, just after it.",
  },
];

export const TextPanel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const mod = useSelector(Selector.basicMod.getVariationDraftText);
  assert(mod !== undefined, "mod is undefined");
  const originalText = useSelectorFactory(Selector.getOriginalTextFactory, mod);
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);
  const errors = useSelector(Selector.getVariationDraftUIErrors);
  const errorColor = theme.palette.text.tonal.error;

  // TODO: wire to actual detection logic
  const [showMultipleElementsWarning] = useState(false);

  const update = (update: Partial<TextMod>) =>
    dispatch(Action.basicMod.setDraft({ ...mod, ...update }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          {originalText && (
            <ClampedText label="Original Text" text={originalText} />
          )}

          <ElementMeta mod={mod} errors={errors} onChange={update} />

          <IgDivider />

          <IgToggleButtonGroup
            buttons={editModeButtons}
            value={mod.editMode}
            sx={{ width: "100%" }}
            control={{
              exclusive: true,
              onChange: (_event, editMode: TextEditMode) =>
                update({ editMode }),
            }}
          />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Panel.Header>
              {mod.editMode === "text" ? "Text changes" : "HTML changes"}
            </Panel.Header>
            <Panel.Checkbox
              label="Hide element"
              checked={mod.remove}
              onChange={(remove) => update({ remove })}
            />
          </Stack>

          {mod.editMode === "text"
            ? !mod.remove && (
                <>
                  <Panel.TextField
                    label={
                      <>
                        Find text
                        <Panel.Optional />
                      </>
                    }
                    value={mod.find}
                    onChange={(e) => update({ find: e.target.value })}
                    placeholder="Find specific text to replace or remove"
                  />

                  <Panel.TextField
                    label="Replace with"
                    autoFocus
                    value={mod.replace}
                    onChange={(e) => update({ replace: e.target.value })}
                    placeholder="Enter text to replace with"
                    error={Boolean(errors?.replace)}
                  />
                </>
              )
            : !mod.remove && (
                <>
                  <Stack spacing={1}>
                    <IgSelect<ModInsertType>
                      fullWidth
                      size="small"
                      value={mod.insertType}
                      onChange={(e) => update({ insertType: e.target.value })}
                    >
                      {htmlActionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </IgSelect>
                    <IgTypography variant="body2" color="text.secondary">
                      {
                        htmlActionOptions.find(
                          (opt) => opt.value === mod.insertType
                        )?.description
                      }
                    </IgTypography>
                  </Stack>

                  <Stack
                    sx={{
                      border: (theme) =>
                        `1px solid ${theme.igBorders.core.default}`,
                      borderRadius: "6px",
                    }}
                  >
                    <IgCodeEditor
                      autoFocus
                      language="html"
                      height="200px"
                      value={mod.html}
                      outline={
                        errors?.html ? `1px solid ${errorColor}` : undefined
                      }
                      onChange={(html) => update({ html })}
                    />
                  </Stack>
                </>
              )}
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={() => void cancel()} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
